import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Inbox, Clock, Loader2, CheckCircle2, ChevronRight, ShieldAlert,
  ClipboardCheck, UserCheck, PlayCircle, AlertTriangle, Gauge, RefreshCw,
} from 'lucide-react';

const VIEW_META = {
  needs_attention: { label: 'Needs my attention', icon: Inbox },
  waiting: { label: 'Waiting on others', icon: Clock },
  in_progress: { label: 'In progress', icon: Loader2 },
  completed: { label: 'Completed', icon: CheckCircle2 },
};

const WORK_TYPE_ICON = {
  validate_recommendation: ClipboardCheck,
  review_approval: UserCheck,
  complete_action: PlayCircle,
  resolve_blocker: AlertTriangle,
  review_result: Gauge,
};

// Priority: color + WORD (never color alone — accessibility).
const PRIORITY_STYLE = {
  critical: 'text-rose-300 bg-rose-500/15 border-rose-500/30',
  high: 'text-amber-300 bg-amber-500/15 border-amber-500/30',
  normal: 'text-sky-300 bg-sky-500/15 border-sky-500/30',
  low: 'text-slate-300 bg-slate-500/15 border-slate-500/30',
};
const CONFIDENCE_STYLE = {
  high: 'text-emerald-300 bg-emerald-500/10',
  moderate: 'text-amber-300 bg-amber-500/10',
  low: 'text-slate-300 bg-slate-500/10',
};

function PriorityBadge({ level, overridden }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${PRIORITY_STYLE[level] || PRIORITY_STYLE.normal}`}
      data-testid="work-priority-badge">
      {level === 'critical' && <ShieldAlert className="w-3 h-3" />}
      {level.charAt(0).toUpperCase() + level.slice(1)} priority
      {overridden && <span className="text-[9px] opacity-70">(overridden)</span>}
    </span>
  );
}

function WorkCard({ card, onOpen }) {
  const Icon = WORK_TYPE_ICON[card.work_type] || ClipboardCheck;
  const cardId = card.action_id || card.rec_id;
  return (
    <button
      onClick={() => onOpen(card)}
      data-testid={`work-card-${cardId}`}
      className="group w-full text-left rounded-2xl border border-border/50 bg-card/40 backdrop-blur p-5 hover:border-primary/40 hover:bg-card/60 transition-all duration-200"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-primary">
          <Icon className="w-3.5 h-3.5" />
          <span data-testid="work-type-label">{card.work_type_label}</span>
        </div>
        <PriorityBadge level={card.priority} overridden={card.priority_overridden} />
      </div>
      <h3 className="mt-2 text-base font-semibold text-foreground leading-snug" data-testid="work-title">{card.title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{card.consequence}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
        <span className="px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground">{card.area}</span>
        <span className={`px-2 py-0.5 rounded-md ${CONFIDENCE_STYLE[card.confidence] || ''}`}>
          {card.confidence.charAt(0).toUpperCase() + card.confidence.slice(1)} confidence
        </span>
        {card.due_date && (
          <span className="px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground">Due {card.due_date}</span>
        )}
        {card.next_actor && (
          <span className="px-2 py-0.5 rounded-md bg-muted/40 text-muted-foreground/90">Next: {card.next_actor}</span>
        )}
      </div>
      {card.elevation && (
        <p className="mt-2 text-[11px] text-muted-foreground/80 italic">{card.elevation}</p>
      )}
      <div className="mt-3 flex items-center gap-1 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
        Open <ChevronRight className="w-3.5 h-3.5" />
      </div>
    </button>
  );
}

export default function MyWorkPage() {
  const navigate = useNavigate();
  const [view, setView] = useState('needs_attention');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [emptyMsg, setEmptyMsg] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getWorkQueue();
      setItems(res.items || []);
      setCounts(res.counts || {});
      setEmptyMsg(res.empty_message || '');
    } catch (e) {
      toast.error(e.message || 'Could not load your work');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = items.filter(i => i.view === view);

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="my-work-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">My Work</h1>
          <p className="text-sm text-muted-foreground mt-1">What genuinely needs your attention today.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="my-work-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={() => navigate('/notifications')} data-testid="my-work-notifications-link"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-muted/50 text-foreground hover:bg-muted transition-colors">
            Notifications
          </button>
          <button onClick={() => navigate('/decision-policies')} data-testid="my-work-policies-link"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-muted/50 text-foreground hover:bg-muted transition-colors">
            Policies
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" role="tablist">
        {Object.entries(VIEW_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const active = view === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => setView(key)}
              data-testid={`work-view-${key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border transition-all ${active ? 'bg-primary/15 text-primary border-primary/30' : 'text-muted-foreground border-border/50 hover:text-foreground hover:bg-muted/40'}`}
            >
              <Icon className="w-4 h-4" />
              {meta.label}
              <span className={`min-w-[20px] px-1.5 rounded-full text-[10px] font-bold ${active ? 'bg-primary/20' : 'bg-muted/60'}`}>{counts[key] || 0}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="my-work-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-border/50 bg-card/30 p-12 text-center" data-testid="my-work-empty">
          <CheckCircle2 className="w-10 h-10 text-emerald-400/70 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-base font-medium text-foreground">
            {view === 'needs_attention' ? 'Nothing needs your attention.' : 'Nothing here right now.'}
          </p>
          {view === 'needs_attention' && (
            <p className="text-sm text-muted-foreground mt-1">
              Routine updates will be included in your Friday digest.
            </p>
          )}
        </div>
      ) : (
        <div className="grid gap-4" data-testid="my-work-list">
          {visible.map(card => (
            <WorkCard key={card.action_id || card.rec_id} card={card}
              onOpen={(c) => c.rec_id && navigate(`/my-work/recommendation/${c.rec_id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}
